import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import axiosClient from '../../api/axios.client.js';
import { sendExtensionAiChatStream } from '../../api/extensions.api.js';
import { fallbackNote } from './flowBuilder/aiModels';
import { CLAW_DEFAULT_AI_MODEL } from '../../constants/clawDefaults.js';

const EXAMPLE_PROMPTS = [
  {
    label: 'Support copilot',
    prompt: 'Build a tenant plugin com.acme.support-copilot with one MCP tool support_lookup (search tickets by id) and a skill support-agent that uses it. Use Ollama qwen2.5-coder:14b defaults.',
  },
  {
    label: 'Research skill',
    prompt: 'Create a skills-only plugin com.example.research with skill web-researcher that may call builtin.web_search. Strong system prompt for citing sources.',
  },
  {
    label: 'Web API tool',
    prompt: 'Create com.example.weather with an HTTP tool com.example.weather.get_forecast that calls the free Open-Meteo API '
      + '(https://api.open-meteo.com/v1/forecast) for a latitude and longitude, and a skill weather-helper that uses it.',
  },
  {
    label: 'Tools pack',
    prompt: 'Create com.example.ops-tools with mcp-tool-bundle: ops_ping and ops_status tools for health checks. No skills yet.',
  },
  {
    label: 'Recruiting add-on',
    prompt: 'Extend the current draft with a skill custom-screener for structured JSON screening output and allowedTools if_export_panel_json if InterviewFlow is enabled.',
  },
];

export default function StudioV2ExtensionBuilderAiPanel({
  manifest,
  onApplyManifest,
}) {
  const [models, setModels] = useState([]);
  const [model, setModel] = useState(CLAW_DEFAULT_AI_MODEL);
  const [messages, setMessages] = useState([
    {
      id: 'welcome',
      role: 'assistant',
      content: 'Describe the plugin, skills, or MCP tools you want. Qwen (Ollama) will propose a CLAW manifest you can apply to the builder.',
    },
  ]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [err, setErr] = useState(null);
  const [lastProposal, setLastProposal] = useState(null);
  const [lastValidation, setLastValidation] = useState(null);
  const [waitedMs, setWaitedMs] = useState(0);
  const bottomRef = useRef(null);
  const streamAbortRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await axiosClient.get('models');
        const list = data?.models || [];
        if (cancelled) return;
        setModels(list);
        const names = list.map((m) => m.name);
        if (names.includes(CLAW_DEFAULT_AI_MODEL)) setModel(CLAW_DEFAULT_AI_MODEL);
        else if (names.length) setModel(names[0]);
      } catch {
        /* optional */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, busy, streaming]);

  useEffect(() => () => {
    streamAbortRef.current?.abort();
  }, []);

  const sendMessage = useCallback(async (text, { clientValidationErrors } = {}) => {
    const t = String(text || '').trim();
    if (!t || busy || streaming) return;
    setErr(null);
    setLastProposal(null);
    setLastValidation(null);
    setWaitedMs(0);
    const userMsg = { id: `u_${Date.now()}`, role: 'user', content: t };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setBusy(true);
    setStreaming(true);

    const historyForApi = [
      ...messages.filter((m) => m.id !== 'welcome' && (m.role === 'user' || m.role === 'assistant')),
      userMsg,
    ].map((m) => ({ role: m.role, content: m.content }));

    const assistantId = `a_${Date.now()}`;
    setMessages((prev) => [...prev, { id: assistantId, role: 'assistant', content: '' }]);
    streamAbortRef.current = new AbortController();

    const {
      reply, error, proposedManifest, proposedValidation, fallback,
    } = await sendExtensionAiChatStream({
      messages: historyForApi,
      model,
      manifest: manifest || undefined,
      clientValidationErrors,
      signal: streamAbortRef.current.signal,
      onEvent: (ev) => {
        if (ev?.type === 'waiting') setWaitedMs(Number(ev.elapsedMs) || 0);
        if (ev?.type === 'token' && typeof ev.chunk === 'string') {
          setMessages((prev) => prev.map((m) => (
            m.id === assistantId ? { ...m, content: `${m.content || ''}${ev.chunk}` } : m
          )));
        }
      },
    });

    streamAbortRef.current = null;
    setBusy(false);
    setStreaming(false);

    if (error) {
      setErr(error);
      setMessages((prev) => prev.filter((m) => m.id !== assistantId));
      return;
    }
    if (!reply) {
      setMessages((prev) => prev.map((m) => (
        m.id === assistantId ? { ...m, content: '(empty response)' } : m
      )));
    }
    const note = fallbackNote(fallback);
    if (note) {
      setMessages((prev) => [...prev, {
        id: `sys_${Date.now()}`, role: 'assistant', content: note,
      }]);
    }
    if (proposedManifest) setLastProposal(proposedManifest);
    if (proposedValidation) setLastValidation(proposedValidation);
  }, [busy, streaming, messages, model, manifest]);

  const onApply = useCallback(() => {
    if (!lastProposal) return;
    onApplyManifest?.(lastProposal, lastValidation);
    if (lastValidation?.valid) {
      setLastProposal(null);
      setLastValidation(null);
    }
    setMessages((prev) => [...prev, {
      id: `sys_${Date.now()}`,
      role: 'assistant',
      content: lastValidation?.valid
        ? 'Applied manifest to the builder. Review fields, Validate, then sideload.'
        : 'Applied draft with validation issues — click Fix validation errors to ask Qwen to repair.',
    }]);
  }, [lastProposal, lastValidation, onApplyManifest]);

  const onFixValidation = useCallback(() => {
    const errors = lastValidation?.errors || [];
    if (!errors.length) return;
    sendMessage(
      `Fix these validation errors in the manifest:\n${errors.map((e) => `- ${e}`).join('\n')}`,
      { clientValidationErrors: errors },
    );
  }, [lastValidation, sendMessage]);

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <AutoFixHighIcon color="primary" fontSize="small" />
        <Typography variant="subtitle2" fontWeight={700}>AI plugin assistant</Typography>
        <Chip size="small" label="Qwen" variant="outlined" />
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5 }}>
        Local Ollama by default — generates MCP tools, skills, and full plugin manifests.
      </Typography>

      <FormControl size="small" sx={{ minWidth: 200, mb: 1.5 }}>
        <InputLabel id="ext-ai-model">Model</InputLabel>
        <Select
          labelId="ext-ai-model"
          label="Model"
          value={model}
          onChange={(e) => setModel(e.target.value)}
          disabled={busy}
        >
          {(models.length ? models : [{ name: CLAW_DEFAULT_AI_MODEL, displayName: 'Qwen 2.5 Coder 14B (Ollama)' }]).map((m) => (
            <MenuItem key={m.name} value={m.name}>{m.displayName || m.name}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        {EXAMPLE_PROMPTS.map((ex) => (
          <Chip
            key={ex.label}
            size="small"
            label={ex.label}
            clickable
            disabled={busy}
            onClick={() => sendMessage(ex.prompt)}
            variant="outlined"
          />
        ))}
      </Stack>

      {err && <Alert severity="error" sx={{ mb: 1 }}>{err}</Alert>}

      <Box
        sx={{
          flex: 1,
          minHeight: 200,
          maxHeight: 360,
          overflow: 'auto',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1.5,
          p: 1.25,
          mb: 1.5,
          bgcolor: 'action.hover',
        }}
      >
        <Stack spacing={1}>
          {messages.map((m) => (
            <Box key={m.id}>
              <Typography variant="caption" fontWeight={700} color={m.role === 'user' ? 'primary.main' : 'text.secondary'}>
                {m.role === 'user' ? 'You' : 'Assistant'}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {m.content}
                {streaming && m.role === 'assistant' && m.content === '' && (
                  <>
                    <CircularProgress size={12} sx={{ ml: 0.5, verticalAlign: 'middle' }} />
                    {waitedMs >= 10000 && (
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }} data-testid="plugin-ai-waiting">
                        {`Writing the manifest… ${Math.floor(waitedMs / 60000)}m ${Math.round((waitedMs % 60000) / 1000)}s. A local model can take several minutes.`}
                      </Typography>
                    )}
                  </>
                )}
              </Typography>
            </Box>
          ))}
          <div ref={bottomRef} />
        </Stack>
      </Box>

      {lastProposal && (
        <Alert
          severity={lastValidation?.valid ? 'success' : 'warning'}
          sx={{ mb: 1.5 }}
          data-testid="plugin-ai-proposal"
          data-valid={lastValidation?.valid ? 'true' : 'false'}
          action={(
            <Stack direction="row" spacing={0.5}>
              {!lastValidation?.valid && (
                <Button color="inherit" size="small" onClick={onFixValidation}>
                  Fix with AI
                </Button>
              )}
              <Button color="inherit" size="small" onClick={onApply} data-testid="plugin-ai-apply">
                Apply to builder
              </Button>
            </Stack>
          )}
        >
          {lastValidation?.valid
            ? `Valid manifest proposed (${lastProposal.id || 'plugin'}).`
            : `Manifest proposed with ${lastValidation?.errors?.length || 0} validation issue(s) — apply to edit in builder.`}
        </Alert>
      )}

      <Stack direction="row" spacing={1}>
        <TextField
          size="small"
          fullWidth
          multiline
          maxRows={4}
          placeholder="e.g. Add a skill that summarizes tickets using my_lookup tool…"
          inputProps={{ 'data-testid': 'plugin-ai-input' }}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={busy}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              sendMessage(input);
            }
          }}
        />
        <Button
          variant="contained"
          onClick={() => sendMessage(input)}
          disabled={busy || !input.trim()}
          sx={{ minWidth: 48 }}
          aria-label="Send"
          data-testid="plugin-ai-send"
        >
          {busy ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
        </Button>
      </Stack>
    </Paper>
  );
}
